// Implements: F-UIX-020, F-UIX-024, F-UIX-007
/**
 * The design a mockup created in a scope will wear, as a chip of the Lienzo hero.
 *
 * A design profile is not chosen when a mockup is created (a backend limit: `POST /mockups/conversation`
 * takes no profile): the server applies the active profile of the creator and of the scope, through
 * the cascade. So the chip is honest about it: "Diseño: <the profile that cascade gives>", and its
 * popover says where it comes from and that it is chosen per scope. A person whom the server lets
 * manage the scope (`may_edit` of the listing, never a role) can activate another profile of that
 * scope right there and is linked to Diseño; everybody else is told to ask the one who administers it.
 *
 * No chip while the listing is unknown (loading, refused) or no scope is chosen: a chip with nothing
 * to name says nothing. The inner part is keyed by the scope (checklist 14): a pick, a pending
 * request or a refusal belongs to ONE scope.
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";
import { Link } from "react-router";

import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import type { TranslationKey } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ChipPopover } from "../../../shared/ui/molecules/ChipPopover";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ProfileListingView } from "../../../types/design";
import { ClosedSelect } from "../components/ClosedSelect";
import { useProfileListing, useScopeActivation } from "../hooks/useDesignProfiles";
import { effectiveDesign } from "../model/designCascade";
import type { DesignSource, SelectedScope } from "../model/designCascade";
import styles from "./ScopeDesignChipContainer.module.css";

const SOURCES = {
  personal: "design.scopeChip.from.personal",
  team: "design.scopeChip.from.team",
  organization: "design.scopeChip.from.organization",
  system: "design.scopeChip.from.system",
} as const satisfies Record<DesignSource, TranslationKey>;

export interface ScopeDesignChipContainerProps {
  /** The scope the mockup will be created in; `null` while none is chosen. */
  readonly scope: SelectedScope | null;
}

export function ScopeDesignChipContainer({ scope }: ScopeDesignChipContainerProps): ReactElement | null {
  const listing = useProfileListing();
  if (scope === null || listing.data === undefined) return null;
  return <ScopeDesignChip key={`${scope.kind}:${scope.id}`} listing={listing.data} scope={scope} />;
}

interface ScopeDesignChipProps {
  readonly listing: ProfileListingView;
  readonly scope: SelectedScope;
}

function ScopeDesignChip({ listing, scope }: ScopeDesignChipProps): ReactElement {
  const { t } = useTranslation();
  const selectId = useId();
  const activation = useScopeActivation();
  const [choice, setChoice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const effective = effectiveDesign(listing, scope);
  const state = listing.scopes.find((candidate) => candidate.scope_kind === scope.kind && candidate.scope_id === scope.id);
  const profiles = (listing.groups.find((group) => group.scope_kind === scope.kind && group.scope_id === scope.id)?.profiles ?? []).map(
    (profile) => ({ value: profile.id, label: profile.name }),
  );
  const mayManage = state?.may_edit === true;
  const chosen =
    profiles.find((profile) => profile.value === choice)?.value ??
    profiles.find((profile) => profile.value === state?.active_profile_id)?.value ??
    profiles[0]?.value ??
    "";

  const activate = async (): Promise<void> => {
    if (state === undefined) return;
    setPending(true);
    setNotice(null);
    setRefusal(null);
    const outcome = await activation.activate(state, chosen);
    setPending(false);
    if (outcome.ok) {
      setNotice(t("design.active.activated", { name: profiles.find((profile) => profile.value === chosen)?.label ?? "" }));
    } else {
      setRefusal(outcome.error);
    }
  };

  return (
    <ChipPopover
      label={t("design.scopeChip.label", { name: effective.name })}
      title={t("design.scopeChip.title")}
      description={t("design.scopeChip.description")}
      leading={<Icon name="palette" size="sm" />}
      width="md"
    >
      <div className={styles.panel}>
        <p>{t(SOURCES[effective.source])}</p>
        <p className={styles.meta}>{t("design.profile.what")}</p>
        <p className={styles.meta}>{t("design.scopeChip.explain")}</p>
        <p className={styles.meta}>{t("design.active.useDefaultMeans")}</p>
        {mayManage ? (
          <>
            {profiles.length === 0 ? (
              <p className={styles.meta}>{t("design.scopeChip.noProfiles")}</p>
            ) : (
              <div className={styles.actions}>
                <ClosedSelect
                  id={selectId}
                  label={t("design.active.choose")}
                  options={profiles}
                  value={chosen}
                  onChange={setChoice}
                  disabled={pending}
                />
                <Button
                  variant="primary"
                  loading={pending}
                  onClick={() => {
                    void activate();
                  }}
                >
                  {t("design.active.useDefault", { scope: state.display_name ?? t("scope.label") })}
                </Button>
              </div>
            )}
            <Link className={styles.link} to="/design/profiles">
              {t("design.scopeChip.manage")}
            </Link>
          </>
        ) : (
          <p className={styles.meta}>{t("design.scopeChip.readOnly")}</p>
        )}
        {notice === null ? null : (
          <p role="status" className={styles.meta}>
            {notice}
          </p>
        )}
        {refusal === null ? null : (
          <p role="alert" className={styles.error}>
            {userMessage(refusal, t("design.active.failed"))}
          </p>
        )}
      </div>
    </ChipPopover>
  );
}
